import { Badge, Box, Flex, Tabs, type BoxProps } from '@chakra-ui/react'
import type { ReactNode } from 'react'

export type TabDef<T extends string> = {
  key: T
  label: ReactNode
  count?: number
}

type TabbedSplitLayoutProps<T extends string> = {
  tab: T
  onTabChange: (t: T) => void
  tabs: Array<TabDef<T>>

  hideLeft?: boolean
  leftFlex?: BoxProps['flex']
  rightFlex?: BoxProps['flex']

  height?: BoxProps['h'] // default 420px
  maxHeight?: BoxProps['maxH']

  left: ReactNode
  right: ReactNode
}

export function TabbedSplitLayout<T extends string>({
  tab,
  onTabChange,
  tabs,
  hideLeft,
  leftFlex = '1',
  rightFlex = '2',
  height = '420px',
  maxHeight,
  left,
  right,
}: TabbedSplitLayoutProps<T>) {
  const showRight = right != null
  return (
    <Flex
      direction="column"
      gap={4}
      minW={0}
      minH={0}
      h={height}
      maxH={maxHeight}
      overflow="hidden"
    >
      {/* Layout wrapper (Chakra) */}
      <Flex direction="column" gap={4} minW={0} minH={0} flex="1" overflow="hidden">
        <Tabs.Root
          value={tab}
          onValueChange={(e: any) => onTabChange(e.value as T)}
          variant="subtle"
        >
          {/* Tabs row */}
          <Box
            overflowX="auto"
            css={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Tabs.List w="max-content" gap={1} flexWrap="nowrap" whiteSpace="nowrap">
              {tabs.map(t => (
                <Tabs.Trigger key={t.key} value={t.key} flexShrink={0}>
                  <Flex align="center" gap={2}>
                    {t.label}
                    {typeof t.count === 'number' ? (
                      <Badge size="sm" variant="subtle">
                        {t.count}
                      </Badge>
                    ) : null}
                  </Flex>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Box>
        </Tabs.Root>

        <Flex
          flex="1"
          minH={0}
          minW={0}
          overflow="hidden"
          gap={{ base: 2, lg: 4 }}
          direction={{ base: 'column', md: 'row' }}
          align="stretch"
        >
          {!hideLeft ? (
            <Flex
              flex={{ base: '1', md: leftFlex }}
              direction="column"
              minW={0}
              minH={0}
              overflow="hidden"
            >
              {left}
            </Flex>
          ) : null}

          {showRight ? (
            <Flex
              flex={hideLeft ? '1' : rightFlex}
              flexDirection="column"
              minW={0}
              minH={0}
              overflow="hidden"
            >
              {right}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  )
}
